import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import VideoCard from '@/components/base/VideoCard';
import { categories, videos, recommendedVideos, latestVideos } from '@/mocks/videos';

export default function Home() {
  const recommended = recommendedVideos.map((id) => videos.find((v) => v.id === id)!).filter(Boolean);
  const latest = latestVideos.map((id) => videos.find((v) => v.id === id)!).filter(Boolean);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '草莓视频在线播放',
    url: 'https://www.caomei-sp.com',
    description: '草莓视频在线播放平台，提供海量高清视频资源，无需下载即可流畅观看。',
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '草莓视频在线播放',
    url: 'https://www.caomei-sp.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.caomei-sp.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '草莓视频在线播放 - 首页',
    description: '草莓视频在线播放平台，提供海量高清视频资源，无需下载即可流畅观看。',
    url: 'https://www.caomei-sp.com/',
  };

  return (
    <>
      <SEOMeta
        title="草莓视频在线播放 - 高清流畅免费看 | 海量视频在线观看平台"
        description="草莓视频在线播放平台，提供海量高清视频资源，无需下载即可流畅观看。支持多种清晰度切换，PC端与手机端全兼容，随时随地享受优质在线播放体验。"
        keywords="草莓视频在线播放,草莓视频高清在线播放,草莓视频免费在线观看,草莓视频手机在线播放,草莓视频在线播放不卡,在线视频播放平台"
        canonicalUrl="https://www.caomei-sp.com/"
        ogTitle="草莓视频在线播放 - 高清流畅免费看"
        ogDescription="草莓视频在线播放平台，提供海量高清视频资源，无需下载即可流畅观看。支持多种清晰度切换，PC端与手机端全兼容。"
        ogUrl="https://www.caomei-sp.com/"
        jsonLd={[organizationSchema, websiteSchema, webPageSchema]}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20stylized%20modern%20home%20theater%20with%20large%20projection%20screen%20displaying%20abstract%20cinematic%20visuals%2C%20cozy%20ambient%20warm%20coral%20and%20amber%20lighting%2C%20minimalist%20interior%20with%20comfortable%20seating%2C%20atmospheric%20cinema%20room%2C%20dark%20walls%20with%20subtle%20warm%20accents%2C%20professional%20architectural%20photography%2C%20cinematic%20mood&width=1920&height=800&seq=hero-bg-home-2025&orientation=landscape"
            alt="草莓视频在线播放 - 畅享高清观影体验"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <p className="inline-block px-4 py-1.5 bg-primary-500/90 text-background-50 text-sm rounded-full mb-6 font-medium">
              海量高清视频 · 免费流畅观看
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-background-50 mb-6 leading-tight">
              草莓视频
              <span className="block text-xl md:text-2xl lg:text-3xl font-body font-light mt-3 text-background-200">
                高清流畅在线播放平台
              </span>
            </h1>
            <p className="text-background-300 text-sm md:text-base max-w-2xl mx-auto mb-8 leading-relaxed">
              汇聚海量高清视频资源，涵盖电影、电视剧、动漫、综艺、纪录片等丰富品类。支持多种清晰度自由切换，
              无需下载安装，PC端与手机端全兼容，随时随地享受流畅观影体验。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/all"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-play-circle-line mr-2"></i>
                立即观影
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 rounded-full text-base font-medium hover:bg-background-50/30 transition-all duration-300 border border-background-50/30 whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>

            <div className="mt-12 flex items-center justify-center gap-8 text-background-300 text-sm">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading text-background-50">10万+</div>
                <div className="mt-1">高清视频</div>
              </div>
              <div className="w-px h-10 bg-background-400/30"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading text-background-50">5000万+</div>
                <div className="mt-1">活跃用户</div>
              </div>
              <div className="w-px h-10 bg-background-400/30"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading text-background-50">4K</div>
                <div className="mt-1">超清画质</div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-8 bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Recommended Section */}
        <section className="py-12 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">精选热门内容，为你推荐最优质的视频</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
              {recommended.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20flowing%20curves%20in%20coral%20and%20amber%20tones%2C%20soft%20de-focused%20cinematic%20bokeh%20lights%2C%20minimal%20elegant%20texture%2C%20modern%20website%20banner%20background%2C%20subtle%20gradient%20with%20depth&width=1920&height=400&seq=cta-banner-home&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-500/85"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-heading text-background-50 mb-4">
              随时随地 · 畅享高清观影
            </h2>
            <p className="text-background-200 text-sm md:text-base max-w-xl mx-auto mb-8">
              草莓视频在线播放支持手机、平板、电脑多端同步观看，智能记忆播放进度，断点续播更方便。
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center px-8 py-3 bg-background-50 text-primary-500 rounded-full text-base font-medium hover:bg-background-100 transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-flashlight-line mr-2"></i>
              开始探索
            </Link>
          </div>
        </section>

        {/* Latest Videos */}
        <section className="py-12 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  最新上线
                </h2>
                <p className="text-sm text-foreground-500 mt-1">每日更新，不错过任何精彩内容</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
              {latest.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-12 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center mb-10">
              为什么选择草莓视频？
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-4k-line', title: '4K超清画质', desc: '支持4K超高清画质播放，细节纤毫毕现' },
                { icon: 'ri-speed-up-line', title: '流畅不卡顿', desc: '智能CDN加速技术，保证播放顺畅无卡顿' },
                { icon: 'ri-smartphone-line', title: '多端兼容', desc: 'PC、手机、平板全端适配，随时随地观看' },
                { icon: 'ri-shield-check-line', title: '安全放心', desc: '严格内容审核机制，绿色健康观影环境' },
              ].map((feature) => (
                <div key={feature.title} className="text-center p-6">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100">
                    <i className={`${feature.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: '草莓视频在线播放需要下载安装吗？',
                  a: '不需要！草莓视频在线播放是纯网页端播放平台，无需下载任何软件或插件，打开浏览器即可流畅观看高清视频。',
                },
                {
                  q: '草莓视频支持哪些清晰度？',
                  a: '我们支持从480P到4K超高清多种清晰度切换，系统会根据您的网络环境智能推荐最佳清晰度，您也可以手动选择。',
                },
                {
                  q: '手机端可以观看吗？',
                  a: '当然可以！草莓视频在线播放完美适配手机、平板和电脑端，无论您使用什么设备，都能获得优质的观看体验。',
                },
                {
                  q: '草莓视频在线播放收费吗？',
                  a: '草莓视频提供大量免费高清内容供用户观看。部分独家内容可能需要会员，但我们始终保证大多数内容免费开放。',
                },
              ].map((faq) => (
                <details key={faq.q} className="group bg-background-100 rounded-lg px-5 py-4 cursor-pointer">
                  <summary className="flex items-center justify-between text-foreground-900 font-medium text-sm">
                    {faq.q}
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                { '@type': 'Question', name: '草莓视频在线播放需要下载安装吗？', acceptedAnswer: { '@type': 'Answer', text: '不需要！草莓视频在线播放是纯网页端播放平台，无需下载任何软件或插件，打开浏览器即可流畅观看高清视频。' } },
                { '@type': 'Question', name: '草莓视频支持哪些清晰度？', acceptedAnswer: { '@type': 'Answer', text: '我们支持从480P到4K超高清多种清晰度切换，系统会根据您的网络环境智能推荐最佳清晰度，您也可以手动选择。' } },
                { '@type': 'Question', name: '手机端可以观看吗？', acceptedAnswer: { '@type': 'Answer', text: '当然可以！草莓视频在线播放完美适配手机、平板和电脑端，无论您使用什么设备，都能获得优质的观看体验。' } },
                { '@type': 'Question', name: '草莓视频在线播放收费吗？', acceptedAnswer: { '@type': 'Answer', text: '草莓视频提供大量免费高清内容供用户观看。部分独家内容可能需要会员，但我们始终保证大多数内容免费开放。' } },
              ],
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}